import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractDialogResultData,
  AbstractFormDialog,
  AbstractFormDialogData,
} from '@next/core-lib/dialog';
import { select, Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  CropFieldSyncDto,
  ExternalCropFieldSources,
  SelectableExternalCropField,
  SelectableExistingCropField,
  SelectableChangedCropField,
  SyncCropFieldsV2Selection,
  SiteState,
  CropVarietiesByCropId,
} from 'src/app/shared/models';

import { isRvOProvider } from '../../../shared/helpers/is-rvo-provider';
import { LoadCropVarietiesByCropIdsRequest } from '../../store/actions/crop-variety.actions';
import { getCropVarietiesByCropId } from '../../store/selectors/crop-variety.selectors';
import { getCropsMap } from '../../store/selectors/crop.selectors';

export interface SyncCropfieldsDialogV2Model extends AbstractFormDialogData {
  syncData: CropFieldSyncDto;
  externalCropFieldSource: ExternalCropFieldSources;
  externalId: string;
  year?: number;
}

type SelectableCropFieldType =
  | SelectableExternalCropField
  | SelectableChangedCropField
  | SelectableExistingCropField;

@Component({
  selector: 'app-sync-cropfields-dialog-v2',
  templateUrl: './sync-cropfields-dialog-v2.component.html',
  styleUrls: ['./sync-cropfields-dialog-v2.component.scss'],
  standalone: false,
})
export class SyncCropfieldsDialogV2Component
  extends AbstractFormDialog<SyncCropFieldsV2Selection>
  implements OnInit, OnDestroy
{
  data = inject<SyncCropfieldsDialogV2Model>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        SyncCropfieldsDialogV2Component,
        AbstractDialogResultData<SyncCropFieldsV2Selection>
      >
    >(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  formGroup = this.fb.group({ year: [this.data.year, [Validators.required]] });

  selectableNewCropFields: SelectableExternalCropField[] = [];
  selectableChangedCropFields: SelectableChangedCropField[] = [];
  selectableDeletedCropFields: SelectableExistingCropField[] = [];

  ngOnInit(): void {
    const cropIds = this.getUsedCropIds();

    setTimeout(() => this.store.dispatch(new LoadCropVarietiesByCropIdsRequest(cropIds)), 0);

    // Initialize selectable new crop fields
    this.selectableNewCropFields = [...this.data.syncData.newCropFields]
      .sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1))
      .map(
        (cropField) =>
          ({
            ...cropField,
            isSelected: true,
            cropName: 'Loading...',
            cropVarietyName: '',
          }) as SelectableExternalCropField,
      );

    // Initialize selectable changed crop fields
    this.selectableChangedCropFields = [...this.data.syncData.changedCropFields]
      .sort((a, b) => (a.item1.name.toLowerCase() > b.item1.name.toLowerCase() ? 1 : -1))
      .map(
        (row) =>
          ({
            external: {
              ...row.item1,
              isSelected: true,
              cropName: 'Loading...',
              cropVarietyName: '',
            },
            existing: {
              ...row.item2,
              isSelected: false,
              cropName: 'Loading...',
              cropVarietyName: '',
            },
            isSelected: true,
          }) as SelectableChangedCropField,
      );

    // Initialize selectable deleted crop fields
    this.selectableDeletedCropFields = [...this.data.syncData.deletedCropFields]
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
      .map(
        (cropField) =>
          ({
            ...cropField,
            isSelected: true,
            cropName: 'Loading...',
            cropVarietyName: '',
          }) as SelectableExistingCropField,
      );

    // Load crop names
    this.store.pipe(select(getCropsMap), takeUntil(this.destroyed$)).subscribe((cropsMap) => {
      for (const cropField of this.selectableNewCropFields) {
        cropField.cropName = this.determineCropName(cropField.cropId, cropsMap);
      }
      for (const cropField of this.selectableChangedCropFields) {
        cropField.external.cropName = this.determineCropName(cropField.external.cropId, cropsMap);
        cropField.existing.cropName = this.determineCropName(cropField.existing.cropId, cropsMap);
      }
      for (const cropField of this.selectableDeletedCropFields) {
        cropField.cropName = this.determineCropName(cropField.cropId, cropsMap);
      }
    });

    // Load crop variety names
    this.store
      .pipe(select(getCropVarietiesByCropId), takeUntil(this.destroyed$))
      .subscribe((cropVarietiesByCropId) => {
        for (const cropField of this.selectableNewCropFields) {
          cropField.cropVarietyName = this.determineCropVarietyName(
            cropField.cropId,
            cropField.cropVarietyId,
            cropVarietiesByCropId,
          );
        }

        for (const cropField of this.selectableChangedCropFields) {
          cropField.external.cropVarietyName = this.determineCropVarietyName(
            cropField.external.cropId,
            cropField.external.cropVarietyId,
            cropVarietiesByCropId,
          );
          cropField.existing.cropVarietyName = this.determineCropVarietyName(
            cropField.existing.cropId,
            cropField.existing.cropVarietyId,
            cropVarietiesByCropId,
          );
        }

        for (const cropField of this.selectableDeletedCropFields) {
          cropField.cropVarietyName = this.determineCropVarietyName(
            cropField.cropId,
            cropField.cropVarietyId,
            cropVarietiesByCropId,
          );
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  confirmSelection(result: SyncCropFieldsV2Selection) {
    result.selectedNewExternalIds = this.selectableNewCropFields
      .filter((f) => f.isSelected)
      .map((f) => f.externalId);

    result.selectedChangedExternalIds = this.selectableChangedCropFields
      .filter((f) => f.isSelected)
      .map((f) => f.external.externalId);

    result.year = this.formGroup.get('year')?.value;
    this.confirm(result);
  }

  getCropNamesFromSelectableCropFieldsWithCropFieldCounts = (
    selectableCropFields: SelectableCropFieldType[],
  ): { [key: string]: number } => {
    return selectableCropFields.reduce(
      (acc: { [key: string]: number }, obj: SelectableCropFieldType) => {
        let key: string;
        if ('external' in obj) {
          // SelectableChangedCropField
          key = obj?.external?.cropName;
        } else {
          // SelectableExternalCropField or SelectableExistingCropField
          key = obj?.cropName;
        }
        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key]++;
        return acc;
      },
      {},
    );
  };

  toggleAllCropFieldsWithCropName(
    cropName: string,
    selectableCropFields: SelectableCropFieldType[],
  ) {
    const isSelected = selectableCropFields.some((cropField) => {
      const fieldCropName =
        'external' in cropField ? cropField.external.cropName : cropField.cropName;
      return (
        fieldCropName === cropName &&
        (cropField.isSelected || ('external' in cropField && cropField.isSelected))
      );
    });

    selectableCropFields.forEach((cropField) => {
      const fieldCropName =
        'external' in cropField ? cropField.external.cropName : cropField.cropName;
      if (fieldCropName === cropName) {
        cropField.isSelected = !isSelected;
      }
    });
  }

  allCropFieldsOfCropTypeSelected = (
    cropName: string,
    cropFieldsOfCropTypeCount: number,
    selectableCropFields: SelectableCropFieldType[],
  ): boolean => {
    const selectedCount = this.getSelectedCropFieldsCount(selectableCropFields, cropName);
    return selectedCount === cropFieldsOfCropTypeCount;
  };

  partiallyCropFieldsOfCropTypeSelected = (
    cropName: string,
    cropFieldsOfCropTypeCount: number,
    selectableCropFields: SelectableCropFieldType[],
  ): boolean => {
    const selectedCount = this.getSelectedCropFieldsCount(selectableCropFields, cropName);
    return selectedCount !== cropFieldsOfCropTypeCount && selectedCount > 0;
  };

  providerName = (source: ExternalCropFieldSources): string => {
    return isRvOProvider(source) ? 'RvO' : 'BMS';
  };

  private getSelectedCropFieldsCount(
    selectableCropFields: SelectableCropFieldType[],
    cropName: string,
  ): number {
    return selectableCropFields.filter((cropField) => {
      const fieldCropName =
        'external' in cropField ? cropField.external.cropName : cropField.cropName;
      return fieldCropName === cropName && cropField.isSelected;
    }).length;
  }

  private determineCropVarietyName(
    cropId: number,
    cropVarietyId: number,
    cropVarietiesByCropId: CropVarietiesByCropId[],
  ) {
    const cropVarieties = cropVarietiesByCropId.find(
      (cropVariety) => cropVariety.id === cropId,
    )?.cropVarieties;
    if (cropVarieties && cropVarietyId) {
      return cropVarieties.find((cropVariety) => cropVariety.id === cropVarietyId)?.name || '';
    }

    return '';
  }

  private determineCropName(cropId: number, cropsMap: { [key: string]: string }) {
    return cropsMap[cropId?.toString() || ''] || `Crop ID: ${cropId || 'Unknown'}`;
  }

  private getUsedCropIds() {
    const newCropIds = this.data.syncData.newCropFields
      .map((cropField) => cropField.cropId)
      .filter((value, index, self) => value && self.indexOf(value) === index);

    const changedCropIds = this.data.syncData.changedCropFields
      .map((row) => row.item1.cropId)
      .filter((value, index, self) => value && self.indexOf(value) === index);

    const deletedCropIds = this.data.syncData.deletedCropFields
      .map((cropField) => cropField.cropId)
      .filter((value, index, self) => value && self.indexOf(value) === index);
    return [...new Set([...newCropIds, ...changedCropIds, ...deletedCropIds])];
  }
}
